import { useState, useEffect, useMemo } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import { colors } from "../source/source.styles";
import DSPHeader from "../detailed/DSPHeader";
import DSPKPICards from "../detailed/DSPKPICards";
import DSPSourceTrendChart from "../detailed/DSPSourceTrendChart";
import DSPLeadQuality from "../detailed/DSPLeadQuality";
import DSPConversionFunnel from "../detailed/DSPConversionFunnel";
import DSPLeadsTable from "../detailed/DSPLeadsTable";
import {
  getServiceDetailApi,
  getServiceLeadsApi,
} from "../../../api/leads.api";
import dayjs from "dayjs";

const ServiceDetailView = ({
  service,
  onBack,
  onExport,
  dateRange,
  onDateChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [leadsData, setLeadsData] = useState([]);

  // Use dateRange from props or default to current month
  const effectiveDateRange = useMemo(
    () =>
      dateRange || {
        startDate: dayjs().startOf("month").toDate(),
        endDate: dayjs().endOf("month").toDate(),
      },
    [dateRange],
  );

  useEffect(() => {
    fetchData();
  }, [service, effectiveDateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: effectiveDateRange.startDate.toISOString(),
        endDate: effectiveDateRange.endDate.toISOString(),
      };

      // Fetch service detail data (aggregated stats)
      const detailResponse = await getServiceDetailApi(service?.id, params);

      // Fetch leads data
      const leadsResponse = await getServiceLeadsApi(service?.id, params);

      // The leads are at: leadsResponse.data.data.leads
      const responseLeads =
        leadsResponse.data?.data?.leads || leadsResponse.data?.data || [];

      setData(detailResponse.data?.data || null);
      setLeadsData(
        Array.isArray(responseLeads?.data?.leads)
          ? responseLeads?.data?.leads
          : [],
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching service detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    onDateChange?.(range);
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          bgcolor: colors.bgPrimary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  // Get service name with proper capitalization
  const serviceName = service?.name
    ? service.name.charAt(0).toUpperCase() + service.name.slice(1)
    : data?.serviceName || "Unknown Service";

  // Get WON stage count from conversion funnel data (use BOOKED as fallback since no WON stage in API response)
  // Data is nested under data.data
  const actualData = data?.data || data;
  const wonLeads =
    actualData?.conversionFunnel?.find((f) => f.stage === "WON")?.value ||
    actualData?.conversionFunnel?.find((f) => f.stage === "BOOKED")?.value ||
    leadsData.filter((lead) => lead.current_status?.key === "won").length ||
    0;

  // Calculate KPI data from API summary data (nested under stats)
  const totalLeads = actualData?.stats?.totalBookings || leadsData.length || 0;
  const totalRevenue =
    actualData?.stats?.totalRevenue ||
    leadsData.reduce((sum, lead) => {
      const payments = lead.payments || [];
      return sum + payments.reduce((pSum, p) => pSum + (p.amount || 0), 0);
    }, 0);
  const conversionRate =
    totalLeads > 0 ? Math.round((wonLeads / totalLeads) * 1000) / 10 : 0;
  const avgRevenuePerLead =
    totalLeads > 0 ? Math.round(totalRevenue / totalLeads) : 0;

  // Build chart data from API timeSeries or generate fallback
  const apiTimeSeriesData = data?.revenueTrend || [];
  const isSingleDate =
    effectiveDateRange.startDate &&
    effectiveDateRange.endDate &&
    new Date(effectiveDateRange.startDate).toDateString() ===
      new Date(effectiveDateRange.endDate).toDateString();

  let chartData;

  if (apiTimeSeriesData && apiTimeSeriesData.length > 0) {
    // Transform revenueTrend data to chart format
    chartData = {
      labels: apiTimeSeriesData.map((d) => d.date),
      leads: apiTimeSeriesData.map((d) => d.value || 0),
      conversions: apiTimeSeriesData.map((d) =>
        Math.round((d.value || 0) * (conversionRate / 100)),
      ),
    };
  } else {
    // Fallback to mock data
    if (isSingleDate) {
      const hours = Array.from(
        { length: 24 },
        (_, i) => i.toString().padStart(2, "0") + ":00",
      );
      const leadsPerHour = Math.max(totalLeads / 24, 1);
      const baseConvRate = conversionRate || 10;

      chartData = {
        labels: hours,
        leads: hours.map((_, i) => {
          const hour = i;
          if (hour >= 9 && hour <= 17) {
            return Math.round(leadsPerHour * (1.5 + Math.random() * 0.5));
          } else if (hour >= 6 && hour <= 20) {
            return Math.round(leadsPerHour * (0.7 + Math.random() * 0.5));
          } else {
            return Math.round(leadsPerHour * (0.2 + Math.random() * 0.4));
          }
        }),
        conversions: hours.map((_, i) => {
          let rate = baseConvRate;
          rate = rate * (0.8 + Math.random() * 0.4);
          return Math.min(Math.round(rate * 10) / 10, 100);
        }),
      };
    } else {
      const dayDiff =
        Math.ceil(
          (new Date(effectiveDateRange.endDate) -
            new Date(effectiveDateRange.startDate)) /
            (1000 * 60 * 60 * 24),
        ) + 1;
      const days = Math.min(dayDiff, 30);
      const labels = [];
      const today = new Date();

      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        labels.push(
          d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        );
      }

      const leadsPerDay = totalLeads / days;
      const convPerDay = wonLeads / days;
      chartData = {
        labels: labels,
        leads: labels.map(() =>
          Math.round(leadsPerDay * (0.5 + Math.random())),
        ),
        conversions: labels.map(
          () => Math.round(convPerDay * (0.5 + Math.random()) * 100) / 100,
        ),
      };
    }
  }

  // Build KPI data
  const kpiData = {
    totalLeads: {
      value: totalLeads,
      trend: "+12.3% vs last month",
      trendType: "up",
    },
    avgRevenuePerLead: {
      value: avgRevenuePerLead,
      trend: "+5.2% vs last month",
      trendType: "up",
    },
    conversion: {
      value: conversionRate,
      count: wonLeads,
      trend: "+0.3 points since last month",
      trendType: "up",
    },
    revenue: {
      value: totalRevenue,
      trend: "+8.5% High performing month",
      trendType: "up",
    },
  };

  // Map legacy statuses to new workflow categories
  const mapStatusToCategory = (statusKey, statusType) => {
    // Valid new workflow statuses
    const validGradeStatuses = ["new", "cold", "warm", "hot"];
    const validPreConsultStatuses = ["qualified", "need_analysis", "booked"];
    const validPostConsultStatuses = [
      "post_consult",
      "proposal_shared",
      "negotiation",
      "treatment_booked",
      "won",
    ];

    // If already using new workflow, return as-is
    if (validGradeStatuses.includes(statusKey)) return `grade_${statusKey}`;
    if (validPreConsultStatuses.includes(statusKey))
      return `preconsult_${statusKey}`;
    if (validPostConsultStatuses.includes(statusKey))
      return `postconsult_${statusKey}`;
    if (statusKey === "dropped") return "dropped";

    // Map legacy statuses to appropriate categories
    const gradeLegacyMap = {
      voicemail: "grade_new",
      contacted: "grade_new",
      interested: "grade_hot",
      positive: "grade_hot",
      highintent: "grade_hot",
      notinterested: "grade_cold",
      not_interested: "grade_cold",
      negative: "grade_cold",
      lowintent: "grade_cold",
      neutral: "grade_warm",
      unreachable: "grade_cold",
      callback: "grade_warm",
      followup: "grade_warm",
      followuplater: "grade_warm",
      busy: "grade_new",
      switchedoff: "grade_cold",
      ringing: "grade_new",
      wrongnumber: "grade_cold",
      spam: "grade_cold",
      testlead: "grade_new",
      duplicate: "grade_cold",
      invalid: "grade_cold",
      abandoned: "grade_cold",
      expired: "grade_cold",
    };

    const preConsultLegacyMap = {
      lead_assigned: "preconsult_new",
      lead_received: "preconsult_new",
      lead_viewed: "preconsult_new",
      not_connected: "preconsult_new",
      connected: "preconsult_qualified",
      discussion: "preconsult_qualified",
      interested: "preconsult_qualified",
      verification_failed: "preconsult_need_analysis",
      verified: "preconsult_qualified",
      unverified: "preconsult_need_analysis",
      enquiryonly: "preconsult_new",
      pricerequested: "preconsult_need_analysis",
      comparing: "preconsult_need_analysis",
      confirmation: "preconsult_booked",
      booking: "preconsult_booked",
    };

    const postConsultLegacyMap = {
      payment_pending: "postconsult_negotiation",
      payment_received: "postconsult_won",
      consultation: "postconsult_post_consult",
      appointment_no_show: "preconsult_booked",
      price_discussion: "postconsult_negotiation",
      proposal: "postconsult_proposal_shared",
      treatment_confirmed: "postconsult_treatment_booked",
      converted: "postconsult_won",
    };

    // Check legacy maps first
    if (gradeLegacyMap[statusKey]) return gradeLegacyMap[statusKey];
    if (preConsultLegacyMap[statusKey]) return preConsultLegacyMap[statusKey];
    if (postConsultLegacyMap[statusKey]) return postConsultLegacyMap[statusKey];

    // Default to grade_new for unknown statuses
    return "grade_new";
  };

  // Calculate actual grade counts from leadsData with status mapping
  const categoryCounts = leadsData.reduce((acc, lead) => {
    const statusKey = lead.current_status?.key || "unknown";
    const statusType = lead.current_status?.type || "grade";
    const category = mapStatusToCategory(statusKey, statusType);
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  // Map categories to expected field names
  const newLeads = categoryCounts.grade_new || 0;
  const hotLeads = categoryCounts.grade_hot || 0;
  const warmLeads = categoryCounts.grade_warm || 0;
  const coldLeads = categoryCounts.grade_cold || 0;
  const qualifiedLeads =
    (categoryCounts.preconsult_qualified || 0) +
    (categoryCounts.preconsult_new || 0);
  const needAnalysisLeads = categoryCounts.preconsult_need_analysis || 0;
  const bookedLeads = categoryCounts.preconsult_booked || 0;
  const postConsultLeads = categoryCounts.postconsult_post_consult || 0;
  const proposalSharedLeads = categoryCounts.postconsult_proposal_shared || 0;
  const negotiationLeads = categoryCounts.postconsult_negotiation || 0;
  const treatmentBookedLeads = categoryCounts.postconsult_treatment_booked || 0;
  const wonLeadsCount = categoryCounts.postconsult_won || 0;
  const droppedLeads = categoryCounts.dropped || 0;

  // Get funnel stages from actualData
  const conversionFunnel = actualData?.conversionFunnel || [];
  const inquiryValue =
    conversionFunnel.find((f) => f.stage === "INQUIRY")?.value || totalLeads;
  const consultationValue =
    conversionFunnel.find((f) => f.stage === "CONSULTATION")?.value || 0;
  const bookedValue =
    conversionFunnel.find((f) => f.stage === "BOOKED")?.value || 0;

  // Transform service data to match DSP components format
  // Create a sources array from conversionFunnel data for DSPLeadQuality and DSPConversionFunnel
  const transformedComponentData = {
    sources: [
      {
        source: serviceName,
        totalLeads: totalLeads,
        qualityScore:
          conversionRate >= 15 ? 90 : conversionRate >= 10 ? 70 : 50,
        qualityLabel:
          conversionRate >= 15
            ? "Excellent"
            : conversionRate >= 10
              ? "Good"
              : "Average",
        // Use actual grade counts from leadsData with legacy status mapping
        newLeads,
        hotLeads,
        warmLeads,
        coldLeads,
        qualifiedLeads,
        needAnalysisLeads,
        bookedLeads,
        postConsultLeads,
        proposalSharedLeads,
        negotiationLeads,
        treatmentBookedLeads,
        wonLeads: wonLeadsCount,
        droppedLeads,
        // Stage-wise breakdown for lead quality
        gradeDropped: droppedLeads, // Dropped from grade stage
        preConsultDropped: 0, // Not tracked separately currently
        postConsultDropped: 0, // Not tracked separately currently
      },
    ],
    summary: {
      totalLeads: totalLeads,
    },
  };

  // Transform leads data to match DSPLeadsTable format (add totalPayment)
  const transformedLeadsData = leadsData.map((lead) => ({
    ...lead,
    totalPayment:
      lead.payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0,
  }));

  return (
    <Box
      sx={{
        bgcolor: colors.bgPrimary,
        minHeight: "100%",
      }}
    >
      <DSPHeader
        onExport={onExport}
        onDateRangeChange={handleDateRangeChange}
        dateRange={effectiveDateRange}
        sourceName={serviceName}
        isService={true}
        onBack={onBack}
      />

      <Box sx={{ p: 4 }}>
        <Box>
          {/* KPI Cards */}
          <Box sx={{ mb: 4 }}>
            <DSPKPICards data={kpiData} />
          </Box>

          {/* Lead Trends Chart */}
          <Box sx={{ mb: 4 }}>
            <DSPSourceTrendChart
              data={chartData}
              dateRange={effectiveDateRange}
            />
          </Box>

          {/* Two Column Layout: Quality + Funnel */}
          <Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 7 }} sx={{ display: "flex" }}>
                <DSPLeadQuality
                  data={
                    transformedComponentData || {
                      sources: [],
                      summary: { totalLeads },
                    }
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
                <DSPConversionFunnel
                  data={
                    transformedComponentData || {
                      sources: [],
                      summary: { totalLeads },
                    }
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {/* Leads Table */}
          <Box sx={{ mt: 10 }}>
            <DSPLeadsTable data={transformedLeadsData} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceDetailView;
